import {
  OpsDocHistoryResponseSchema,
  OpsDocRevisionResponseSchema,
  OpsDocAccessUpdateSchema,
  OpsDocArchiveRequestSchema,
  OpsDocArchiveResponseSchema,
  OpsDocGroupInputSchema,
  OpsDocGroupResponseSchema,
  OpsDocLinkTargetsResponseSchema,
  OpsDocPageCreateSchema,
  OpsDocPageResponseSchema,
  OpsDocPageUpdateSchema,
  OpsDocsAskRequestSchema,
  OpsDocsAskResponseSchema,
  OpsDocsBootstrapResponseSchema,
  OpsDocsLinkedResponseSchema,
  OpsDocsSearchRequestSchema,
  OpsDocsSearchResponseSchema,
  OpsDocSpaceCreateSchema,
  OpsDocSpaceResponseSchema,
  type OpsDocAccessUpdate,
  type OpsDocGroupInput,
  type OpsDocPageCreate,
  type OpsDocPageUpdate,
  type OpsDocsAskRequest,
  type OpsDocsSearchRequest,
  type OpsDocSpaceCreate,
} from "../../shared/operations-docs-schemas";
import { parseResponse } from "../api/client-core";

const DOCS_ROOT = "/api/ops/docs";

function jsonRequest(method: "POST" | "PUT", body: unknown): RequestInit {
  return {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export async function getOpsDocsBootstrap() {
  return parseResponse(
    await fetch(`${DOCS_ROOT}/bootstrap`, { credentials: "same-origin" }),
    OpsDocsBootstrapResponseSchema,
  );
}

export async function createOpsDocsSpace(input: OpsDocSpaceCreate) {
  return parseResponse(
    await fetch(`${DOCS_ROOT}/spaces`, jsonRequest("POST", OpsDocSpaceCreateSchema.parse(input))),
    OpsDocSpaceResponseSchema,
  );
}

export async function createOpsDocsPage(input: OpsDocPageCreate) {
  return parseResponse(
    await fetch(`${DOCS_ROOT}/pages`, jsonRequest("POST", OpsDocPageCreateSchema.parse(input))),
    OpsDocPageResponseSchema,
  );
}

export async function getOpsDocsPage(pageId: string) {
  return parseResponse(
    await fetch(`${DOCS_ROOT}/pages/${encodeURIComponent(pageId)}`, {
      credentials: "same-origin",
    }),
    OpsDocPageResponseSchema,
  );
}

export async function updateOpsDocsPage(pageId: string, input: OpsDocPageUpdate) {
  return parseResponse(
    await fetch(
      `${DOCS_ROOT}/pages/${encodeURIComponent(pageId)}`,
      jsonRequest("PUT", OpsDocPageUpdateSchema.parse(input)),
    ),
    OpsDocPageResponseSchema,
  );
}

export async function updateOpsDocsPageAccess(pageId: string, input: OpsDocAccessUpdate) {
  return parseResponse(
    await fetch(
      `${DOCS_ROOT}/pages/${encodeURIComponent(pageId)}/access`,
      jsonRequest("PUT", OpsDocAccessUpdateSchema.parse(input)),
    ),
    OpsDocPageResponseSchema,
  );
}

export async function archiveOpsDocsPage(pageId: string, expectedRevision: number) {
  return parseResponse(
    await fetch(
      `${DOCS_ROOT}/pages/${encodeURIComponent(pageId)}/archive`,
      jsonRequest("POST", OpsDocArchiveRequestSchema.parse({ expectedRevision })),
    ),
    OpsDocArchiveResponseSchema,
  );
}

export async function searchInternalDocs(input: OpsDocsSearchRequest) {
  return parseResponse(
    await fetch(
      `${DOCS_ROOT}/search`,
      jsonRequest("POST", OpsDocsSearchRequestSchema.parse(input)),
    ),
    OpsDocsSearchResponseSchema,
  );
}

export async function askInternalDocs(input: OpsDocsAskRequest) {
  return parseResponse(
    await fetch(`${DOCS_ROOT}/ask`, jsonRequest("POST", OpsDocsAskRequestSchema.parse(input))),
    OpsDocsAskResponseSchema,
  );
}

export async function createOpsDocsGroup(input: OpsDocGroupInput) {
  return parseResponse(
    await fetch(`${DOCS_ROOT}/groups`, jsonRequest("POST", OpsDocGroupInputSchema.parse(input))),
    OpsDocGroupResponseSchema,
  );
}

export async function updateOpsDocsGroup(groupId: string, input: OpsDocGroupInput) {
  return parseResponse(
    await fetch(
      `${DOCS_ROOT}/groups/${encodeURIComponent(groupId)}`,
      jsonRequest("PUT", OpsDocGroupInputSchema.parse(input)),
    ),
    OpsDocGroupResponseSchema,
  );
}

export async function listOpsDocsLinkTargets() {
  return parseResponse(
    await fetch(`${DOCS_ROOT}/link-targets`, { credentials: "same-origin" }),
    OpsDocLinkTargetsResponseSchema,
  );
}

export async function listLinkedInternalDocs(input: {
  entityType: "customer" | "tool";
  entityId: string;
}) {
  const query = new URLSearchParams(input);
  return parseResponse(
    await fetch(`${DOCS_ROOT}/linked?${query.toString()}`, { credentials: "same-origin" }),
    OpsDocsLinkedResponseSchema,
  );
}

export async function getOpsDocHistory(pageId: string, before?: number) {
  const query = before ? `?before=${before}` : "";
  return parseResponse(
    await fetch(`${DOCS_ROOT}/pages/${encodeURIComponent(pageId)}/history${query}`, {
      credentials: "same-origin",
    }),
    OpsDocHistoryResponseSchema,
  );
}

export async function getOpsDocRevision(pageId: string, revision: number) {
  return parseResponse(
    await fetch(`${DOCS_ROOT}/pages/${encodeURIComponent(pageId)}/history/${revision}`, {
      credentials: "same-origin",
    }),
    OpsDocRevisionResponseSchema,
  );
}
